import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Factory,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { coreServices as fallbackCoreServices, pageSeo, siteInfo } from "../data";
import { getServices } from "../cms/serviceQueries";

function setMetaTag(selector, attributes) {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
}

function setCanonical(url) {
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);
}

function setStructuredData(data) {
  const scriptId = "higa-page-structured-data";
  let script = document.getElementById(scriptId);

  if (!script) {
    script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

function usePageSeo(service, coreServices) {
  useEffect(() => {
    const seo = service
      ? {
          title: service.seo.title,
          description: service.seo.description,
          canonical: `${siteInfo.url}/services/${service.slug}`,
          image: service.heroImage || service.image,
        }
      : {
          ...pageSeo.services,
          image: coreServices[0]?.heroImage || coreServices[0]?.image,
        };

    document.title = seo.title;

    setMetaTag('meta[name="description"]', {
      name: "description",
      content: seo.description,
    });

    setMetaTag('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });

    setMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: seo.title,
    });

    setMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: seo.description,
    });

    setMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: seo.canonical,
    });

    setMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: seo.image,
    });

    setMetaTag('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });

    setMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: seo.title,
    });

    setMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: seo.description,
    });

    setCanonical(seo.canonical);

    if (service) {
      setStructuredData({
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.title,
        description: service.description,
        url: seo.canonical,
        image: service.heroImage || service.image,
        provider: {
          "@type": "Organization",
          name: siteInfo.legalName,
          url: siteInfo.url,
          telephone: siteInfo.phone,
          email: siteInfo.email,
        },
        areaServed: {
          "@type": "Country",
          name: "India",
        },
        serviceType: service.title,
      });
    } else {
      setStructuredData({
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "HIG Automation Services",
        description: pageSeo.services.description,
        url: pageSeo.services.canonical,
        itemListElement: coreServices.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `${siteInfo.url}/services/${item.slug}`,
        })),
      });
    }
  }, [service, coreServices]);
}

function ServicesOverview({ coreServices }) {
  usePageSeo(null, coreServices);

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden bg-[#0a1929] text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage: `url(${coreServices[0]?.heroImage || coreServices[0]?.image})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1929] via-[#0a1929]/90 to-[#0a1929]/55" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28">
          <div className="text-[#ff6b7a] uppercase tracking-[0.3em] text-xs font-bold mb-4">
            Engineering Services
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6 max-w-5xl">
            Industrial Automation & Engineering Services
          </h1>

          <p className="max-w-3xl text-slate-300 text-lg leading-relaxed">
            From PLC and SCADA automation to custom machines, conveyors,
            industrial utilities, control panels and fabrication, HIG Automation
            provides integrated engineering support from concept through
            commissioning.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <div className="text-[#c8102e] uppercase tracking-[0.3em] text-xs font-bold mb-4">
              Our Capabilities
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0a1929] tracking-tight mb-5">
              Explore Our Services
            </h2>

            <p className="text-slate-600 leading-relaxed">
              Select a service to view our approach, key benefits, typical use
              cases and the solutions we deliver for industrial projects.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8">
            {coreServices.map((service) => (
              <article
                key={service.slug}
                className="group bg-white border border-slate-200 hover:border-[#c8102e] transition-all hover:shadow-2xl overflow-hidden flex flex-col"
              >
                <div className="aspect-[16/9] overflow-hidden bg-slate-200">
                  <img
                    src={service.image}
                    alt={`${service.title} engineering services by HIG Automation`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>

                <div className="p-7 flex flex-col flex-1">
                  <div className="text-[#c8102e] uppercase tracking-[0.2em] text-[10px] font-bold mb-3">
                    {service.eyebrow}
                  </div>

                  <h2 className="font-black text-[#0a1929] text-xl tracking-tight mb-3">
                    {service.title}
                  </h2>

                  <p className="text-slate-600 text-sm leading-relaxed mb-5">
                    {service.tagline}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {service.bullets.slice(0, 3).map((bullet) => (
                      <li
                        key={bullet}
                        className="flex gap-2 text-sm text-slate-700"
                      >
                        <ChevronRight className="w-4 h-4 text-[#c8102e] mt-0.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={`/services/${service.slug}`}
                    className="mt-auto inline-flex items-center gap-2 text-[#c8102e] font-bold text-sm uppercase tracking-wide hover:gap-3 transition-all"
                  >
                    View Details
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#c8102e] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-black text-3xl lg:text-4xl mb-4">
            Have a Custom Engineering Requirement?
          </h2>

          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Share your process, machine or plant requirement with our
            engineering team. We will review the application and discuss a
            practical execution approach.
          </p>

          <Link to="/contact">
            <Button className="bg-white text-[#c8102e] hover:bg-slate-100 rounded-none px-10 py-6 font-bold uppercase">
              Talk To An Engineer
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

function ServiceDetail({ service, coreServices }) {
  usePageSeo(service, coreServices);

  return (
    <div className="bg-white">
      <section className="relative bg-[#0a1929] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url(${service.heroImage || service.image})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1929] via-[#0a1929]/92 to-[#0a1929]/45" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28">
          <div className="text-[#ff6b7a] uppercase tracking-[0.3em] text-xs font-bold mb-4">
            {service.eyebrow}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6 max-w-5xl">
            {service.title}
          </h1>

          <p className="max-w-4xl text-slate-300 text-lg leading-relaxed">
            {service.description}
          </p>

          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/contact">
              <Button className="bg-[#c8102e] hover:bg-[#a80d26] text-white rounded-none px-8 py-6 font-bold uppercase">
                Request a Quote
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>

            <a href={`tel:${siteInfo.phone}`}>
              <Button
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white hover:text-[#0a1929] rounded-none px-8 py-6 font-bold uppercase"
              >
                Call Our Team
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="text-[#c8102e] uppercase tracking-[0.3em] text-xs font-bold mb-4">
              How We Help
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0a1929] tracking-tight mb-6">
              Engineered Around Your Process
            </h2>

            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              Our team starts with the operating requirement, existing plant
              conditions and expected production outcome. Mechanical,
              electrical, automation and site-execution requirements are then
              coordinated into a practical project plan.
            </p>

            <ul className="grid sm:grid-cols-2 gap-4">
              {service.bullets.map((bullet) => (
                <li
                  key={bullet}
                  className="flex gap-3 border border-slate-200 p-4 text-slate-700"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#c8102e] flex-shrink-0 mt-0.5" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-5 bg-slate-50 border-t-4 border-[#c8102e] p-8">
            <Factory className="w-10 h-10 text-[#c8102e] mb-5" />

            <h2 className="text-2xl font-black text-[#0a1929] mb-5">
              Typical Applications
            </h2>

            <ul className="space-y-4">
              {service.useCases.map((useCase) => (
                <li key={useCase} className="flex gap-3 text-slate-700">
                  <ChevronRight className="w-5 h-5 text-[#c8102e] flex-shrink-0 mt-0.5" />
                  <span>{useCase}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[#c8102e] uppercase tracking-[0.3em] text-xs font-bold mb-4">
              Our Scope
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0a1929] tracking-tight mb-5">
              {service.title} Solutions
            </h2>

            <p className="text-slate-600 leading-relaxed">
              The scope below is specific to {service.title}. Contact our team
              with your application details for a project-specific proposal.
            </p>
          </div>

          <div
            className={`grid md:grid-cols-2 ${
              service.sections.length >= 3 ? "xl:grid-cols-3" : ""
            } gap-8`}
          >
            {service.sections.map((section) => (
              <article
                key={section.title}
                className="group bg-white border border-slate-200 hover:border-[#c8102e] hover:shadow-xl transition-all overflow-hidden"
              >
                <div className="aspect-[16/9] overflow-hidden bg-slate-200">
                  <img
                    src={section.image}
                    alt={`${section.title} for ${service.title} by HIG Automation`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>

                <div className="p-7">
                  <h2 className="text-2xl font-black text-[#0a1929] mb-3">
                    {section.title}
                  </h2>

                  <p className="text-slate-600 leading-relaxed mb-5">
                    {section.description}
                  </p>

                  <ul className="space-y-2">
                    {section.items.map((item) => (
                      <li
                        key={item}
                        className="flex gap-2 text-sm text-slate-700"
                      >
                        <ChevronRight className="w-4 h-4 text-[#c8102e] mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="text-[#c8102e] uppercase tracking-[0.3em] text-xs font-bold mb-4">
                Project Value
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0a1929] tracking-tight mb-5">
                Benefits of Our Approach
              </h2>

              <p className="text-slate-600 leading-relaxed">
                We combine design, fabrication, controls and site execution so
                responsibilities remain clear and the solution is developed
                around the actual manufacturing requirement.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {service.benefits.map((benefit) => (
                <div
                  key={benefit}
                  className="border-l-4 border-[#c8102e] bg-slate-50 p-6"
                >
                  <CheckCircle2 className="w-6 h-6 text-[#c8102e] mb-4" />

                  <h2 className="font-bold text-[#0a1929] leading-relaxed">
                    {benefit}
                  </h2>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#c8102e] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-black text-3xl lg:text-4xl mb-4">
            Need a Custom {service.title} Solution?
          </h2>

          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Share your project requirement, process challenge or existing
            equipment details. Our engineering team will review the application
            and discuss the next steps with you.
          </p>

          <Link to="/contact">
            <Button className="bg-white text-[#c8102e] hover:bg-slate-100 rounded-none px-10 py-6 font-bold uppercase">
              Talk To An Engineer
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default function Services() {
  const { slug } = useParams();
  const [coreServices, setCoreServices] = useState(fallbackCoreServices);

  useEffect(() => {
    let isMounted = true;

    async function loadServices() {
      try {
        const cmsServices = await getServices();

        if (isMounted && Array.isArray(cmsServices) && cmsServices.length > 0) {
          setCoreServices(cmsServices);
        }
      } catch (error) {
        console.error("Unable to load services from Sanity:", error);
      }
    }

    loadServices();

    return () => {
      isMounted = false;
    };
  }, []);

  if (!slug) {
    return <ServicesOverview coreServices={coreServices} />;
  }

  const service = coreServices.find((item) => item.slug === slug);

  if (!service) {
    return (
      <div className="min-h-[70vh] bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-xl">
          <div className="text-[#c8102e] uppercase tracking-[0.3em] text-xs font-bold mb-4">
            Service Not Found
          </div>

          <h1 className="text-4xl lg:text-5xl font-black text-[#0a1929] mb-5">
            We Could Not Find That Service
          </h1>

          <p className="text-slate-600 mb-8">
            The service link may be outdated. View our complete services
            portfolio to find the engineering solution you need.
          </p>

          <Link to="/services">
            <Button className="bg-[#0a1929] hover:bg-[#14213d] text-white rounded-none px-8 py-6 font-bold uppercase">
              View All Services
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return <ServiceDetail service={service} coreServices={coreServices} />;
}